import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Calendar as CalendarIcon, MessageCircle } from 'lucide-react';
import { EmployeePopover } from '../ui/employee-popover';
import {
  TASK_PRIORITY_CLASSES,
  TASK_PRIORITY_LABELS,
  TaskPriority,
  formatDate,
  getDueDateColorClass,
  getInitials,
} from './projectUtils';

export interface TaskCardData {
  id: string;
  name: string;
  description?: string | null;
  priority: TaskPriority;
  startDate?: string | null;
  plannedEndDate?: string | null;
  columnId: string;
  orderInColumn: number;
  commentsCount?: number;
  unreadCommentsCount?: number;
  assignees?: Array<{
    id: string;
    employeeId: string;
    employee?: {
      id: string;
      firstName: string;
      lastName: string;
    } | null;
  }> | null;
}

interface TaskCardProps {
  task: TaskCardData;
  onClick: (task: TaskCardData) => void;
  isDragging?: boolean;
}

export const TaskCard: React.FC<TaskCardProps> = ({
  task,
  onClick,
  isDragging,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: dndIsDragging,
  } = useSortable({ id: task.id, data: { type: 'task', task } });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: dndIsDragging || isDragging ? 0.5 : 1,
  };

  const assignees = task.assignees || [];
  const visibleAssignees = assignees.slice(0, 3);
  const overflowCount = Math.max(0, assignees.length - 3);

  const dueColor = getDueDateColorClass(task.plannedEndDate);

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={(e) => {
        // Avoid triggering click during drag
        if (dndIsDragging) return;
        e.stopPropagation();
        onClick(task);
      }}
      className="group cursor-grab active:cursor-grabbing bg-background border border-border rounded-md p-3 shadow-sm hover:shadow-md hover:border-primary/40 transition-all"
    >
      <div className="flex items-start justify-between gap-2">
        <h4
          className="text-sm font-medium leading-snug line-clamp-2 flex-1"
          title={task.name}
        >
          {task.name}
        </h4>
        <span
          className={`shrink-0 text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${
            TASK_PRIORITY_CLASSES[task.priority] || ''
          }`}
        >
          {TASK_PRIORITY_LABELS[task.priority] || task.priority}
        </span>
      </div>

      <div className="mt-2 flex items-center justify-between gap-2">
        {/* Avatars */}
        <div className="flex -space-x-1.5">
          {visibleAssignees.map((a) => {
            const emp = a.employee;
            const initials = getInitials(emp?.firstName, emp?.lastName);
            const fullName = emp ? `${emp.firstName} ${emp.lastName}` : '';
            const avatar = (
              <div className="w-6 h-6 rounded-full bg-primary/20 text-primary text-[10px] font-semibold flex items-center justify-center border border-background">
                {initials}
              </div>
            );
            if (!emp) {
              return <div key={a.id}>{avatar}</div>;
            }
            return (
              <EmployeePopover
                key={a.id}
                employeeId={emp.id}
                fallbackName={fullName}
              >
                {avatar}
              </EmployeePopover>
            );
          })}
          {overflowCount > 0 && (
            <div className="w-6 h-6 rounded-full bg-muted text-muted-foreground text-[10px] font-semibold flex items-center justify-center border border-background">
              +{overflowCount}
            </div>
          )}
          {assignees.length === 0 && (
            <span className="text-[10px] text-muted-foreground italic">
              nieprzypisane
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Comments icon */}
          {(task.commentsCount ?? 0) > 0 && (
            <div className="relative flex items-center gap-1 text-xs text-muted-foreground">
              <MessageCircle className="w-3.5 h-3.5" />
              <span>{task.commentsCount}</span>
              {(task.unreadCommentsCount ?? 0) > 0 && (
                <span
                  className="absolute -top-1.5 -right-2 min-w-[14px] h-[14px] px-1 rounded-full bg-primary text-primary-foreground text-[9px] font-bold leading-[14px] text-center"
                  title={`${task.unreadCommentsCount} nieprzeczytanych`}
                >
                  {task.unreadCommentsCount}
                </span>
              )}
            </div>
          )}

          {/* Due date */}
          {task.plannedEndDate && (
            <div className={`flex items-center gap-1 text-xs ${dueColor}`}>
              <CalendarIcon className="w-3 h-3" />
              <span>{formatDate(task.plannedEndDate)}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
